import { useEffect, useRef, useState } from "react";

import { useAppFeatures } from "@/hooks/useAppFeatures";
import { isGlobalLogoutPending } from "@/utils/sso";

import useSsoLogin from "./useSsoLogin";

/**
 * @param shouldStart caller's own "unauthenticated and done checking" signal.
 * @returns `isRedirecting` — true while a redirect to the IdP is pending, so the
 * caller should render a loading state rather than its unauthenticated UI.
 */
export const useSsoAutoStart = (shouldStart: boolean) => {
  const { isLokasiSsoAuthEnabled } = useAppFeatures();
  const { startSso } = useSsoLogin();

  // Set when startSso can't proceed (no provider, or the provider fetch failed).
  const [providerAvailable, setProviderAvailable] = useState(true);

  // One attempt per mount. `startSso` navigates away on success, so a second
  // attempt would only ever be a redirect loop (e.g. the IdP bouncing us back
  // unauthenticated).
  const hasAttempted = useRef(false);

  // A global logout in flight also renders as "unauthenticated", but that one is
  // already navigating to the IdP logout endpoint. Starting SSO here would race
  // it — and win, because the provider lookup resolves after our redirect is
  // assigned — sending the user back through a still-live IdP session.
  const isRedirecting =
    isLokasiSsoAuthEnabled &&
    providerAvailable &&
    shouldStart &&
    !isGlobalLogoutPending();

  useEffect(() => {
    if (!isRedirecting || hasAttempted.current) return;

    hasAttempted.current = true;
    startSso().catch(() => setProviderAvailable(false));
  }, [isRedirecting, startSso]);

  return { isRedirecting };
};

export default useSsoAutoStart;
